import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import LoadingOverlay from "react-loading-overlay-ts";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Layout from "../layout/Layout";
import { Addpromotion, GetPromotion, deletePromotion, updatePromotion } from "../Redux/Action/PromoCode";
import { adminupload, careseekerlist } from "../Redux/Action/CareSeekerAction";
import "./Offer.css";

const formatUserLabel = (user, index) => {
  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const email = user?.email || "";
  const phone =
    user?.phone?.country_code && user?.phone?.number
      ? `${user.phone.country_code} ${user.phone.number}`
      : "";

  let labelParts = [];
  if (name) labelParts.push(name);
  if (email) labelParts.push(email);
  if (phone) labelParts.push(phone);

  return labelParts.length > 0 ? labelParts.join(" | ") : `User ${index + 1}`;
};

const UserSelectionDropdown = ({ options, selected, onChange, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (option) => {
    const isSelected = selected.some((s) => s.value === option.value);
    if (isSelected) {
      onChange(selected.filter((s) => s.value !== option.value));
    } else {
      onChange([...selected, option]);
    }
  };

  const handleSelectAll = () => {
    if (filteredOptions.length === 0) return;
    const allFilteredSelected = filteredOptions.every((opt) =>
      selected.some((s) => s.value === opt.value)
    );

    if (allFilteredSelected) {
      const visibleValues = new Set(filteredOptions.map((o) => o.value));
      onChange(selected.filter((s) => !visibleValues.has(s.value)));
    } else {
      const newSelected = [...selected];
      filteredOptions.forEach((opt) => {
        if (!newSelected.some((s) => s.value === opt.value)) {
          newSelected.push(opt);
        }
      });
      onChange(newSelected);
    }
  };

  return (
    <div className="custom-dropdown-container" ref={wrapperRef}>
      <div
        className={`custom-dropdown-header ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>
          {selected.length === 0
            ? "Select users..."
            : `${selected.length} user${selected.length !== 1 ? "s" : ""} selected`}
        </span>
        <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`} />
      </div>
      {isOpen && (
        <div className="custom-dropdown-menu">
          <div className="custom-dropdown-search">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="custom-dropdown-list">
            {filteredOptions.length > 0 && (
              <div
                className="custom-dropdown-item select-all"
                onClick={handleSelectAll}
              >
                <input
                  type="checkbox"
                  checked={
                    filteredOptions.length > 0 &&
                    filteredOptions.every((opt) =>
                      selected.some((s) => s.value === opt.value)
                    )
                  }
                  readOnly
                />
                <strong>Select All</strong>
              </div>
            )}
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="custom-dropdown-item"
                onClick={() => toggleOption(option)}
              >
                <input
                  type="checkbox"
                  checked={selected.some((s) => s.value === option.value)}
                  readOnly
                />
                <span>{option.label}</span>
              </div>
            ))}
            {isLoading ? (
              <div className="custom-dropdown-no-results">Loading users...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="custom-dropdown-no-results">No users found</div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Offer() {
  const dispatch = useDispatch();
  const promotions = useSelector(
    (state) => state.Promotion?.promotionlist || []
  );
  const userList = useSelector((state) => state.careseeker.careseekerArr);

  const [search, setSearch] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState("10");
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [form, setForm] = useState({
    title: "",
    type: "percentage",
    rate: "",
    picture: "",
    description: "",
    flashMessage: "",
    startDate: "",
    endDate: "",
    maxRedemptions: "",
    noEndDate: false,
    isReusable: false,
    scope: "global",
    selectedUsers: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [viewUsersModal, setViewUsersModal] = useState({
    show: false,
    users: [],
    promotionTitle: ""
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    dispatch(GetPromotion())
      .unwrap()
      .catch((error) => {
        const message =
          typeof error === "string" ? error : error && error.message ? error.message : "";
        if (typeof message === "string") {
          const lower = message.toLowerCase();
          if (lower === "no promotions found" || lower === "no promotion found") {
            return;
          }
        }
        if (error) {
          console.error("Failed to load promotions:", error);
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (showCreateModal && (!userList || !userList.data)) {
      setUsersLoading(true);
      dispatch(
        careseekerlist({
          filters: { role: ["care seeker", "carer"], is_deleted: false },
          limit: 100,
          page: 1
        })
      )
        .unwrap()
        .catch((err) => console.error("Failed to load users", err))
        .finally(() => setUsersLoading(false));
    }
  }, [dispatch, showCreateModal, userList]);

  const resetForm = () => {
    setForm({
      title: "",
      type: "percentage",
      rate: "",
      picture: "",
      description: "",
      startDate: "",
      endDate: "",
      maxRedemptions: "",
      noEndDate: false,
      isReusable: false,
      scope: "global",
      selectedUsers: [],
      flashMessage: ""
    });
    setFormErrors({});
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setEditingPromotion(null);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingPromotion(null);
    resetForm();
  };

  const validateForm = () => {
    const errors = {};
    if (!form.title.trim()) errors.title = "Title is required";
    if (!form.rate) errors.rate = "Discount value is required";
    if (!form.picture.trim()) errors.picture = "Image URL is required";
    if (!form.startDate) errors.startDate = "Start date is required";
    if (!form.noEndDate && !form.endDate) errors.endDate = "End date is required";
    if (form.scope === "targeted" && form.selectedUsers.length === 0) {
      errors.selectedUsers = "Select at least one user";
    }
    if (!errors.rate && form.rate) {
      const rateValue = Number(form.rate);
      if (!Number.isFinite(rateValue) || rateValue <= 0) {
        errors.rate = "Discount value must be greater than zero";
      } else if (form.type === "percentage" && rateValue > 99) {
        errors.rate = "Percentage discount cannot exceed 99%";
      }
    }
    if (form.maxRedemptions !== "") {
      const maxValue = Number(form.maxRedemptions);
      if (!Number.isFinite(maxValue) || maxValue <= 0) {
        errors.maxRedemptions = "Maximum usage must be greater than zero";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateOffer = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }
    setCreating(true);
    try {
      const basePayload = {
        title: form.title.trim(),
        type: form.type,
        rate: String(form.rate),
        picture: form.picture.trim(),
        description: form.description.trim(),
        flashMessage: form.flashMessage.trim() || undefined,
        startDate: new Date(form.startDate).toISOString(),
        endDate: form.noEndDate ? undefined : new Date(form.endDate).toISOString(),
        isReusable: String(form.isReusable),
        isGlobal: form.scope === "global"
      };
      if (form.maxRedemptions !== "") {
        basePayload.maxRedemptions = String(form.maxRedemptions);
      }
      if (form.scope === "global") {
        basePayload.userId = [];
      } else {
        basePayload.userId = form.selectedUsers.map((u) => u.value);
      }
      if (editingPromotion && editingPromotion._id) {
        const updatePayload = {
          title: basePayload.title,
          rate: basePayload.rate,
          picture: basePayload.picture,
          description: basePayload.description,
          isReusable: basePayload.isReusable,
          flashMessage: basePayload.flashMessage,
          isGlobal: basePayload.isGlobal,
          userId: basePayload.userId
        };
        if (Object.prototype.hasOwnProperty.call(basePayload, "maxRedemptions")) {
          updatePayload.maxRedemptions =
            basePayload.maxRedemptions == null ? null : basePayload.maxRedemptions;
        }
        updatePayload.endDate = form.noEndDate ? null : basePayload.endDate;
        await dispatch(
          updatePromotion({ id: editingPromotion._id, data: updatePayload })
        ).unwrap();
        toast.success("Offer updated successfully");
      } else {
        await dispatch(Addpromotion(basePayload)).unwrap();
        toast.success("Offer created successfully");
      }
      resetForm();
      setEditingPromotion(null);
      setShowCreateModal(false);
      await dispatch(GetPromotion()).unwrap();
    } catch (error) {
      console.error("Failed to save offer:", error);
      // Note: Thunks already toast API errors, but we catch here to ensure safety
      // and handle any non-API errors.
      if (error && typeof error !== 'string') {
          toast.error(error.message || "An unexpected error occurred");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    const fileInfo = new FormData();
    fileInfo.append("file", file);
    setUploadingImage(true);
    dispatch(adminupload(fileInfo)).then((info) => {
      const url = info?.payload?.url;
      if (url) {
        setForm((prev) => ({ ...prev, picture: url }));
        setFormErrors((prev) => ({ ...prev, picture: undefined }));
      }
      setUploadingImage(false);
    });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this offer?"
    );
    if (!confirmed) {
      return;
    }
    setLoading(true);
    try {
      await dispatch(deletePromotion(id)).unwrap();
      await dispatch(GetPromotion()).unwrap();
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promotion) => {
    const startDate = promotion?.startDate
      ? moment(promotion.startDate).format("YYYY-MM-DD")
      : "";
    const endDate = promotion?.endDate
      ? moment(promotion.endDate).format("YYYY-MM-DD")
      : "";
    const maxRedemptions =
      promotion?.maxRedemptions == null ? "" : String(promotion.maxRedemptions);
    const scope = promotion?.isGlobal ? "global" : "targeted";
    const selectedUsers = Array.isArray(promotion?.assignedUsers)
      ? promotion.assignedUsers.map((user, index) => ({
          label: formatUserLabel(user, index),
          value: user?._id || index
        }))
      : [];
    setForm({
      title: promotion?.title || "",
      type: promotion?.type || "percentage",
      rate: promotion?.rate != null ? String(promotion.rate) : "",
      picture: promotion?.picture || "",
      description: promotion?.description || "",
      startDate,
      endDate,
      maxRedemptions,
      noEndDate: !promotion?.endDate,
      isReusable: !!promotion?.isReusable,
      scope,
      selectedUsers,
      flashMessage: promotion?.flashMessage || ""
    });
    setFormErrors({});
    setEditingPromotion(promotion);
    setShowCreateModal(true);
  };

  const filteredPromotions = useMemo(() => {
    if (!search) {
      return promotions;
    }
    const term = search.toLowerCase();
    return promotions.filter((p) => {
      const title = p?.title || "";
      const code = p?.promoCode || "";
      const desc = p?.description || "";
      return (
        title.toLowerCase().includes(term) ||
        code.toLowerCase().includes(term) ||
        desc.toLowerCase().includes(term)
      );
    });
  }, [promotions, search]);

  const stats = useMemo(() => {
    const total = promotions.length;
    const activeNow = promotions.filter((p) => {
      const now = new Date();
      const start = p?.startDate ? new Date(p.startDate) : null;
      const end = p?.endDate ? new Date(p.endDate) : null;
      if (!p?.isActive || p?.isDeleted) {
        return false;
      }
      if (start && now < start) {
        return false;
      }
      if (end && now > end) {
        return false;
      }
      return true;
    }).length;
    const globalCount = promotions.filter((p) => p?.isGlobal).length;
    const totalRedemptions = promotions.reduce((sum, p) => {
      const count =
        p?.totalRedemptions != null
          ? p.totalRedemptions
          : p?.redemptionCount || 0;
      return sum + count;
    }, 0);
    return { total, activeNow, globalCount, totalRedemptions };
  }, [promotions]);

  const getStatusInfo = (promotion) => {
    const now = new Date();
    const start = promotion?.startDate ? new Date(promotion.startDate) : null;
    const end = promotion?.endDate ? new Date(promotion.endDate) : null;

    if (promotion?.isDeleted || promotion?.isActive === false) {
      return { label: "Inactive", className: "inactive", icon: "fa-circle" };
    }

    if (start && now < start) {
      return { label: "Upcoming", className: "upcoming", icon: "fa-clock" };
    }

    if (end && now > end) {
      return { label: "Expired", className: "expired", icon: "fa-times-circle" };
    }

    return { label: "Active", className: "active", icon: "fa-check-circle" };
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }
    return moment(value).format("DD MMM YYYY, HH:mm");
  };

  const parseDescription = (value) => {
    if (!value) {
      return "";
    }
    return value.replace(/<[^>]+>/g, "");
  };

  const handleViewUsers = (users, title) => {
    setViewUsersModal({
      show: true,
      users,
      promotionTitle: title
    });
  };

  return (
    <LoadingOverlay active={loading} text="Loading offers...">
      <Layout>
        <div className="offers-container">
          <div className="offers-header">
            <div className="header-content">
              <div className="header-left">
                <h1 className="page-title">
                  <i className="fas fa-gift" />
                  Offers
                </h1>
                <p className="page-subtitle">
                  Create and manage promotional offers for care seekers and carers.
                </p>
              </div>
              <div className="header-actions">
                <button
                  type="button"
                  className="btn-primary-modern"
                  onClick={handleOpenCreateModal}
                >
                  <i className="fas fa-plus" />
                  New Offer
                </button>
              </div>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon total">
                <i className="fas fa-layer-group" />
              </div>
              <div className="stat-content">
                <h3>{stats.total}</h3>
                <p>Total Offers</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon active">
                <i className="fas fa-bolt" />
              </div>
              <div className="stat-content">
                <h3>{stats.activeNow}</h3>
                <p>Active Now</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon global">
                <i className="fas fa-globe-europe" />
              </div>
              <div className="stat-content">
                <h3>{stats.globalCount}</h3>
                <p>Global Offers</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon redemptions">
                <i className="fas fa-ticket-alt" />
              </div>
              <div className="stat-content">
                <h3>{stats.totalRedemptions}</h3>
                <p>Total Redemptions</p>
              </div>
            </div>
          </div>

          <div className="filters-section">
            <div className="search-container-modern">
              <div className="search-input-wrapper">
                <span className="search-icon">
                  <i className="fas fa-search" />
                </span>
                <input
                  type="text"
                  className="search-input-modern"
                  placeholder="Search offers by title, code, or status..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search ? (
                  <button
                    type="button"
                    className="clear-search"
                    onClick={() => setSearch("")}
                  >
                    <i className="fas fa-times" />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="filter-controls">
              <div className="items-per-page">
                <label htmlFor="offers-items-per-page">Rows per page</label>
                <select
                  id="offers-items-per-page"
                  className="select-modern"
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(e.target.value)}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
          </div>

          <div className="table-container-modern">
            <div className="table-wrapper">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Offer</th>
                    <th>Audience</th>
                    <th>Discount</th>
                    <th>Usage</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPromotions.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="no-data">
                          <div className="no-data-content">
                            <i className="fas fa-gift" />
                            <h3>No offers found</h3>
                            <p>
                              Use the New Offer button to create a promotion, or
                              adjust your filters.
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredPromotions.map((promotion) => {
                      const statusInfo = getStatusInfo(promotion);
                      const maxRedemptions =
                        promotion?.maxRedemptions == null
                          ? "Unlimited"
                          : promotion.maxRedemptions;
                    const usedCount =
                      promotion?.totalRedemptions != null
                        ? promotion.totalRedemptions
                        : promotion?.redemptionCount || 0;
                      const plainDescription = parseDescription(
                        promotion?.description
                      );
                      return (
                        <tr
                          key={promotion?._id}
                          className="table-row-modern"
                        >
                          <td>
                            <div className="offer-info">
                              <div className="offer-image">
                                {promotion?.picture ? (
                                  <img
                                    src={promotion.picture}
                                    alt={promotion.title || "Offer"}
                                  />
                                ) : (
                                  <span className="no-image">
                                    <i className="fas fa-image" />
                                  </span>
                                )}
                              </div>
                              <div className="offer-text">
                                <h4>{promotion?.title || "Untitled offer"}</h4>
                                {plainDescription ? (
                                  <p className="offer-description">
                                    {plainDescription}
                                  </p>
                                ) : null}
                                <div className="promo-code">
                                  <span>Code:</span>
                                  <code
                                    onClick={() => {
                                      if (promotion?.promoCode) {
                                        navigator.clipboard.writeText(promotion.promoCode);
                                        toast.success("Code copied to clipboard!");
                                      }
                                    }}
                                    title="Click to copy"
                                  >
                                    {promotion?.promoCode}
                                    <i className="fas fa-copy" style={{ marginLeft: "6px", fontSize: "10px" }} />
                                  </code>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="target-users">
                              <span
                                className={
                                  promotion?.isGlobal
                                    ? "scope-badge global"
                                    : "scope-badge user"
                                }
                              >
                                <i className="fas fa-users" />
                                {promotion?.isGlobal ? "Global" : "Targeted"}
                              </span>
                              <div className="audience-meta">
                                <small>
                                  {promotion?.isGlobal
                                    ? "Applies to all eligible users"
                                    : `Assigned to ${
                                        Array.isArray(promotion?.assignedUsers)
                                          ? promotion.assignedUsers.length
                                          : 0
                                      } users`}
                                </small>
                              </div>
                              {!promotion?.isGlobal &&
                              Array.isArray(promotion?.assignedUsers) &&
                              promotion.assignedUsers.length > 0 ? (
                                <div className="user-list">
                                  <div className="user-names">
                                    {promotion.assignedUsers.slice(0, 2).map((user) => {
                                      const name = `${user?.first_name || ""} ${
                                        user?.last_name || ""
                                      }`.trim();
                                      const email = user?.email || "";
                                      const phone =
                                        user?.phone?.country_code && user?.phone?.number
                                          ? `${user.phone.country_code} ${user.phone.number}`
                                          : "";

                                      return (
                                        <a
                                          key={user?._id}
                                          href={`/users/${user?._id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="user-name"
                                          style={{
                                            display: "block",
                                            marginBottom: "8px",
                                            textDecoration: "none",
                                            borderBottom: "1px solid #eee",
                                            paddingBottom: "4px"
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontWeight: "600",
                                              color: "#034328"
                                            }}
                                          >
                                            {name || "View user"}
                                          </div>
                                          {email && (
                                            <div
                                              style={{
                                                fontSize: "11px",
                                                color: "#6b7280"
                                              }}
                                            >
                                              <i
                                                className="fas fa-envelope"
                                                style={{ marginRight: "4px" }}
                                              />
                                              {email}
                                            </div>
                                          )}
                                          {phone && (
                                            <div
                                              style={{
                                                fontSize: "11px",
                                                color: "#6b7280"
                                              }}
                                            >
                                              <i
                                                className="fas fa-phone"
                                                style={{ marginRight: "4px" }}
                                              />
                                              {phone}
                                            </div>
                                          )}
                                        </a>
                                      );
                                    })}
                                    {promotion.assignedUsers.length > 2 && (
                                      <div
                                        onClick={() => handleViewUsers(promotion.assignedUsers, promotion.title)}
                                        style={{
                                          color: "#2B8B6F",
                                          fontSize: "12px",
                                          cursor: "pointer",
                                          fontWeight: "600",
                                          marginTop: "4px",
                                          padding: "4px",
                                          background: "#f0fdf4",
                                          borderRadius: "6px",
                                          textAlign: "center",
                                          border: "1px dashed #bbf7d0"
                                        }}
                                      >
                                        + {promotion.assignedUsers.length - 2} more users
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div>
                              <div
                                className={`type-badge ${
                                  promotion?.type || "percentage"
                                }`}
                              >
                                {promotion?.type || "percentage"}
                              </div>
                              <div className="discount-value">
                                {promotion?.type === "fixed_amount" ||
                                promotion?.type === "fixed_price"
                                  ? `$${promotion?.rate}`
                                  : `${promotion?.rate}%`}
                              </div>
                              <div className="reusability-indicator">
                                <span
                                  className={
                                    promotion?.isReusable
                                      ? "scope-badge global"
                                      : "scope-badge user"
                                  }
                                >
                                  <i className="fas fa-redo" />
                                  {promotion?.isReusable
                                    ? "Reusable per user"
                                    : "Single use per user"}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="performance-metrics">
                              <div className="redemption-count">
                                <i className="fas fa-ticket-alt" />
                                <span>
                                  {usedCount} / {maxRedemptions}
                                </span>
                              </div>
                              <div className="usage-meta">
                                <div className="usage-item">
                                  <small>Total redemptions</small>
                                  <span>{usedCount}</span>
                                </div>
                                <div className="usage-item">
                                  <small>Total cap</small>
                                  <span>
                                    {maxRedemptions === "Unlimited"
                                      ? "Unlimited uses"
                                      : `${maxRedemptions} uses`}
                                  </span>
                                </div>
                                <div className="usage-item">
                                  <small>Per user</small>
                                  <span>
                                    {promotion?.isReusable ? "Reusable" : "Single use"}
                                  </span>
                                </div>
                              </div>
                              <div className="date-range">
                                <div className="date-item">
                                  <small>Starts</small>
                                  <span>{formatDate(promotion?.startDate)}</span>
                                </div>
                                <div className="date-item">
                                  <small>Ends</small>
                                  <span>{formatDate(promotion?.endDate)}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="status-container">
                              <span
                                className={`status-badge ${statusInfo.className}`}
                              >
                                <i className={`fas ${statusInfo.icon}`} />
                                {statusInfo.label}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="btn-action edit"
                                onClick={() => handleEdit(promotion)}
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                className="btn-action delete"
                                onClick={() => handleDelete(promotion?._id)}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="results-info">
                Showing {filteredPromotions.length} of {promotions.length}{" "}
                offers
              </div>
            </div>
          </div>
        </div>
        <Modal
          show={showCreateModal}
          onHide={handleCloseModal}
          dialogClassName="modern-modal"
          centered
        >
          <div className="modal-header-modern">
            <h5 className="modal-title">
              <i className="fas fa-gift" />
              {editingPromotion ? "Edit Offer" : "Create New Offer"}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleCloseModal}
            />
          </div>
          <div className="modal-body-modern">
            <div className="form-grid">
              <div className="form-column">
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-heading" />
                    Title
                  </label>
                  <input
                    type="text"
                    className={`form-control-modern ${
                      formErrors.title ? "error" : ""
                    }`}
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Summer sale promo"
                  />
                  {formErrors.title ? (
                    <div className="error-message">{formErrors.title}</div>
                  ) : null}
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-percentage" />
                    Discount Type
                  </label>
                  <select
                    className="form-control-modern select"
                    disabled={!!editingPromotion}
                    value={form.type}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, type: e.target.value }))
                    }
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed_amount">Fixed Amount</option>
                    <option value="fixed_price">Fixed Price</option>
                  </select>
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-tag" />
                    Discount Value
                  </label>
                  <input
                    type="number"
                    className={`form-control-modern ${
                      formErrors.rate ? "error" : ""
                    }`}
                    value={form.rate}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, rate: e.target.value }))
                    }
                    placeholder={form.type === "percentage" ? "10" : "5"}
                    min="0"
                  />
                  {formErrors.rate ? (
                    <div className="error-message">{formErrors.rate}</div>
                  ) : null}
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-ticket-alt" />
                    Maximum Uses (optional)
                  </label>
                  <input
                    type="number"
                    className={`form-control-modern ${
                      formErrors.maxRedemptions ? "error" : ""
                    }`}
                    value={form.maxRedemptions}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        maxRedemptions: e.target.value
                      }))
                    }
                    placeholder="Leave blank for unlimited"
                    min="1"
                  />
                  {formErrors.maxRedemptions ? (
                    <div className="error-message">
                      {formErrors.maxRedemptions}
                    </div>
                  ) : null}
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-redo" />
                    Allow Multiple Uses
                  </label>
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={form.isReusable}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          isReusable: e.target.checked
                        }))
                      }
                    />
                    Allow the same user to use this code multiple times
                  </label>
                </div>
                <div className="form-row">
                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <i className="fas fa-calendar-plus" />
                      Start Date
                    </label>
                    <input
                      type="date"
                      className={`form-control-modern ${
                        formErrors.startDate ? "error" : ""
                      }`}
                      value={form.startDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          startDate: e.target.value
                        }))
                      }
                    />
                    {formErrors.startDate ? (
                      <div className="error-message">
                        {formErrors.startDate}
                      </div>
                    ) : null}
                  </div>
                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <i className="fas fa-calendar-check" />
                      End Date
                    </label>
                    <input
                      type="date"
                      className={`form-control-modern ${
                        formErrors.endDate ? "error" : ""
                      }`}
                      disabled={form.noEndDate}
                      value={form.endDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          endDate: e.target.value
                        }))
                      }
                    />
                    {formErrors.endDate ? (
                      <div className="error-message">
                        {formErrors.endDate}
                      </div>
                    ) : null}
                    <div style={{ marginTop: 8 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={form.noEndDate}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              noEndDate: e.target.checked,
                              endDate: e.target.checked ? "" : prev.endDate
                            }))
                          }
                        />
                        No end date (perpetual)
                      </label>
                    </div>
                  </div>
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-image" />
                    Image
                  </label>
                  <div className="image-upload-container">
                    <div
                      className="image-upload-area"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                          const file = e.dataTransfer.files[0];
                          const event = { target: { files: [file] } };
                          handleImageFileChange(event);
                        }
                      }}
                    >
                      {form.picture ? (
                        <div className="image-preview">
                          <img src={form.picture} alt={form.title || "Offer"} />
                          <div className="image-overlay">
                            <button
                              type="button"
                              className="change-image-btn"
                            >
                              <i className="fas fa-sync-alt" />
                              Change image
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <i className="fas fa-cloud-upload-alt" />
                          <p>Drag & drop or click to upload</p>
                          <small>JPG, PNG, or WEBP</small>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="file-input-hidden"
                        onChange={handleImageFileChange}
                      />
                    </div>
                    {uploadingImage ? (
                      <div className="upload-progress">
                        <i className="fas fa-spinner fa-spin" />
                        Uploading image...
                      </div>
                    ) : null}
                  </div>
                  {formErrors.picture ? (
                    <div className="error-message">{formErrors.picture}</div>
                  ) : null}
                </div>
              </div>
              <div className="form-column">
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-users" />
                    Audience
                  </label>
                  <div className="form-row">
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="radio"
                        name="scope"
                        value="global"
                        checked={form.scope === "global"}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            scope: e.target.value
                          }))
                        }
                      />
                      Global (all eligible users)
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="radio"
                        name="scope"
                        value="targeted"
                        checked={form.scope === "targeted"}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            scope: e.target.value
                          }))
                        }
                      />
                      Specific users
                    </label>
                  </div>
                  {form.scope === "targeted" ? (
                    <div className="multi-select-modern">
                      <UserSelectionDropdown
                        isLoading={usersLoading}
                        options={(Array.isArray(userList?.data)
                          ? userList.data
                          : Array.isArray(userList)
                          ? userList
                          : []
                        ).map((u, i) => ({
                          label: formatUserLabel(u, i),
                          value: u?._id || i
                        }))}
                        selected={form.selectedUsers}
                        onChange={(selected) =>
                          setForm((prev) => ({ ...prev, selectedUsers: selected }))
                        }
                      />
                      <div className="selected-users-pills">
                        {form.selectedUsers.map((user) => (
                          <span key={user.value} className="user-pill">
                            {user.label}
                            <button
                              type="button"
                              className="user-pill-remove"
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  selectedUsers: prev.selectedUsers.filter(
                                    (u) => u.value !== user.value
                                  )
                                }));
                              }}
                            >
                              <i className="fas fa-times" />
                            </button>
                          </span>
                        ))}
                      </div>
                      {formErrors.selectedUsers ? (
                        <div className="error-message">
                          {formErrors.selectedUsers}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="info-box">
                      <i className="fas fa-info-circle" />
                      This offer will be available globally to all users who meet
                      the eligibility rules.
                    </div>
                  )}
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-align-left" />
                    Description
                  </label>
                  <textarea
                    className="form-control-modern textarea"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value
                      }))
                    }
                    maxLength={250}
                    placeholder="Short description shown to users"
                  />
                  <div className="char-counter">
                    {form.description.length}/250
                  </div>
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">
                    <i className="fas fa-bolt" />
                    Flash ribbon text (optional)
                  </label>
                  <input
                    type="text"
                    className="form-control-modern"
                    value={form.flashMessage}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        flashMessage: e.target.value
                      }))
                    }
                    maxLength={80}
                    placeholder="Shown on the app ribbon when no end date is set"
                  />
                  <div className="char-counter">
                    {form.flashMessage.length}/80
                  </div>
                </div>
                <div className="preview-card">
                  <h4>
                    <i className="fas fa-eye" />
                    Preview
                  </h4>
                  <div className="offer-preview">
                    <div className="preview-image">
                      {form.picture ? (
                        <img src={form.picture} alt={form.title || "Offer"} />
                      ) : (
                        <span className="no-preview-image">
                          <i className="fas fa-image" />
                        </span>
                      )}
                    </div>
                    <div className="preview-content">
                      <h5>{form.title || "Offer title"}</h5>
                      <p>{form.description || "Offer description"}</p>
                      <div className="preview-discount">
                        {form.type === "fixed_amount" || form.type === "fixed_price"
                          ? `$${form.rate || 0}`
                          : `${form.rate || 0}% off`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer-modern">
            <button
              type="button"
              className="btn-secondary-modern"
              onClick={handleCloseModal}
              disabled={creating}
            >
              <i className="fas fa-times" />
              Cancel
            </button>
            <button
              type="button"
              className="btn-submit-modern"
              onClick={handleCreateOffer}
              disabled={creating}
            >
              <i className="fas fa-check" />
              {creating
                ? editingPromotion
                  ? "Saving..."
                  : "Creating..."
                : editingPromotion
                ? "Save Changes"
                : "Create Offer"}
            </button>
          </div>
        </Modal>

        <Modal
          show={viewUsersModal.show}
          onHide={() => setViewUsersModal({ ...viewUsersModal, show: false })}
          className="modern-modal"
          centered
        >
          <div className="modal-header-modern">
            <h3 className="modal-title">
              <i className="fas fa-users" />
              Assigned Users
            </h3>
            <button
              type="button"
              className="btn-close"
              onClick={() => setViewUsersModal({ ...viewUsersModal, show: false })}
            />
          </div>
          <div className="modal-body-modern" style={{ padding: "24px" }}>
            <div style={{ marginBottom: "16px", color: "#6b7280", fontSize: "14px" }}>
              Users assigned to <strong>{viewUsersModal.promotionTitle || "this offer"}</strong>:
            </div>
            <div className="user-list-full">
              {viewUsersModal.users.map((user) => {
                const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
                const email = user?.email || "";
                const phone =
                  user?.phone?.country_code && user?.phone?.number
                    ? `${user.phone.country_code} ${user.phone.number}`
                    : "";
                
                return (
                  <div 
                    key={user?._id || Math.random()} 
                    style={{ 
                      padding: "12px", 
                      borderBottom: "1px solid #f3f4f6",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}
                  >
                    <div 
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: "#f0fdf4",
                        color: "#059669",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: "600"
                      }}
                    >
                      {name ? name.charAt(0).toUpperCase() : <i className="fas fa-user" />}
                    </div>
                    <div>
                      <div style={{ fontWeight: "600", color: "#111827", fontSize: "14px" }}>
                        {name || "Unknown User"}
                      </div>
                      <div style={{ display: "flex", gap: "12px", fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                        {email && (
                          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <i className="fas fa-envelope" style={{ fontSize: "10px" }} />
                            {email}
                          </span>
                        )}
                        {phone && (
                          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <i className="fas fa-phone" style={{ fontSize: "10px" }} />
                            {phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <a 
                      href={`/users/${user?._id}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        marginLeft: "auto", 
                        color: "#2B8B6F", 
                        fontSize: "13px", 
                        textDecoration: "none",
                        fontWeight: "500"
                      }}
                    >
                      View Profile <i className="fas fa-external-link-alt" style={{ fontSize: "10px", marginLeft: "4px" }} />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="modal-footer-modern">
            <button
              type="button"
              className="btn-secondary-modern"
              onClick={() => setViewUsersModal({ ...viewUsersModal, show: false })}
            >
              Close
            </button>
          </div>
        </Modal>
      </Layout>
    </LoadingOverlay>
  );
}
